import type { AnalyticsProvider, TrackInput } from "../types";

// PostHog capture API. Works from client or server — publishable project
// key can safely ship to the browser.
// Env: POSTHOG_KEY (or VITE_POSTHOG_KEY), POSTHOG_HOST (default https://us.i.posthog.com)
export const posthogAnalyticsProvider: AnalyticsProvider = {
  name: "posthog",
  async track(input: TrackInput) {
    const key = readKey();
    const host = readHost();
    if (!key) {
      console.error("[analytics:posthog] key_missing");
      throw new Error("posthog_key_missing");
    }
    const eventId = randomHex(32);
    const res = await fetch(`${host}/capture/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: key,
        event: input.event,
        distinct_id: input.userId ?? "anonymous",
        properties: {
          ...(input.properties ?? {}),
          $insert_id: eventId,
          environment: readEnvName(),
          release: readRelease(),
        },
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    });
    if (!res.ok) {
      throw new Error(`posthog_ingest_status_${res.status}`);
    }
    return eventId;
  },
  async identify(userId, traits) {
    const key = readKey();
    const host = readHost();
    if (!key) throw new Error("posthog_key_missing");
    const res = await fetch(`${host}/capture/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: key,
        event: "$identify",
        distinct_id: userId,
        properties: { $set: traits ?? {} },
      }),
      keepalive: true,
    });
    if (!res.ok) {
      throw new Error(`posthog_ingest_status_${res.status}`);
    }
  },
};

function readKey(): string | undefined {
  if (typeof process !== "undefined" && process.env?.POSTHOG_KEY) return process.env.POSTHOG_KEY;
  // Backwards compatibility with older naming.
  if (typeof process !== "undefined" && process.env?.POSTHOG_API_KEY) return process.env.POSTHOG_API_KEY;
  if (typeof import.meta !== "undefined") {
    const v = (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_POSTHOG_KEY;
    if (v) return v;
  }
  return undefined;
}

function readHost(): string {
  if (typeof process !== "undefined" && process.env?.POSTHOG_HOST) return process.env.POSTHOG_HOST;
  if (typeof import.meta !== "undefined") {
    const v = (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_POSTHOG_HOST;
    if (v) return v;
  }
  return "https://us.i.posthog.com";
}

function readEnvName(): string {
  if (typeof process !== "undefined") {
    return process.env?.POSTHOG_ENVIRONMENT ?? process.env?.NODE_ENV ?? "production";
  }
  return "production";
}

function readRelease(): string | undefined {
  if (typeof process !== "undefined") {
    return process.env?.APP_VERSION ?? process.env?.SENTRY_RELEASE;
  }
  return undefined;
}

function randomHex(len: number) {
  const bytes = new Uint8Array(len / 2);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
